// A5: pure formatting helpers for the admin product-stats cards. Kept out of the React component
// so they can be unit-tested in the admin's node-environment vitest (the JWT-gated UI itself
// can't be driven headlessly — see admin/vitest.config.ts).

import type { CoverageRow } from "@/hooks/useProductStats"

/** Compact integer formatting (1234 → "1,234"). Coerces first — the API can serialize a numeric
 * column as a string, and an absent value as null; a bare toLocaleString would render "NaN". */
export function fmtInt(n: number): string {
  return (Math.round(Number(n)) || 0).toLocaleString("en-US")
}

/** Format a percentage to 1 decimal place with a trailing % (e.g. 70 → "70%", 22.5 → "22.5%"). */
export function fmtPct(n: number): string {
  const v = Number(n)
  if (!Number.isFinite(v)) return "0%"
  // Drop a trailing ".0" so whole numbers read cleanly.
  return `${Number(v.toFixed(1))}%`
}

/** Humanize an event name for display (snake_case → Title Case). Pure. */
export function humanizeEvent(event: string): string {
  return event
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

/**
 * Humanize a `platform` bucket for the breakdown cards (#1500). Pure. The vocabulary is closed
 * (`web` | `ios` | `chopper` | `backend`) plus the `(untagged)` bucket; an unrecognized value is
 * passed through verbatim rather than hidden, so a rogue emitter shows up on the panel.
 */
export function humanizePlatform(platform: string): string {
  switch (platform) {
    case "web":
      return "Web"
    case "ios":
      return "iOS"
    case "chopper":
      return "Chopper"
    case "backend":
      return "Backend"
    case "(untagged)":
      return "Untagged"
    default:
      return platform
  }
}

/** Humanize a capture_mode bucket for the coverage card. Pure. */
export function humanizeCaptureMode(mode: string): string {
  switch (mode) {
    case "identified":
      return "identified"
    case "anon_consent_locale":
      return "anon (consent locale)"
    case "anon_logged_out":
      return "anon (logged out)"
    case "(untagged)":
      return "untagged"
    default:
      return mode
  }
}

/**
 * Render a coverage row as the owner-requested one-line summary, e.g.
 * "Upload Completed: 70% identified — 22% anon (consent locale), 8% anon (logged out)".
 * Pure — the exact copy the acceptance criteria call for. The leading event + identified % is the
 * headline; the remaining capture_mode shares follow after an em dash.
 */
export function coverageSummary(row: CoverageRow): string {
  const rest = row.shares
    .filter((s) => s.mode !== "identified")
    .map((s) => `${fmtPct(s.pct)} ${humanizeCaptureMode(s.mode)}`)
  const head = `${humanizeEvent(row.event)}: ${fmtPct(row.identifiedPct)} identified`
  return rest.length > 0 ? `${head} — ${rest.join(", ")}` : head
}
